import { Link } from "react-router-dom";
import { motion } from "framer-motion";

type Item = {
  name: string;
  path: string;
  bg: string;       // gradient classes
  ring: string;     // decorative ring color
  textColor?: string;
};

const rows: { title: string; subtitle: string; items: Item[] }[] = [
  {
    title: "Foundations",
    subtitle: "Start here",
    items: [
      { name: "ABC & Numbers", path: "/alphabet", bg: "from-pink-400 via-rose-400 to-rose-500", ring: "bg-yellow-300" },
      { name: "English", path: "/english-learning", bg: "from-violet-400 via-purple-400 to-purple-600", ring: "bg-pink-300" },
      { name: "Math Skills", path: "/math", bg: "from-amber-400 via-orange-400 to-orange-500", ring: "bg-lime-300" },
    ],
  },
  {
    title: "Early Thinking",
    subtitle: "Think & explore",
    items: [
      { name: "Colors & Shapes", path: "/shapes-patterns", bg: "from-cyan-400 via-sky-400 to-blue-500", ring: "bg-yellow-300" },
      { name: "Science", path: "/science", bg: "from-indigo-400 via-blue-500 to-blue-600", ring: "bg-emerald-300" },
      { name: "Brain Games", path: "/brain-teasers", bg: "from-fuchsia-400 via-pink-500 to-pink-600", ring: "bg-amber-300" },
    ],
  },
  {
    title: "Discovery",
    subtitle: "Big wide world",
    items: [
      { name: "World Explorer", path: "/world-explorer", bg: "from-emerald-400 via-teal-400 to-teal-600", ring: "bg-yellow-300" },
      { name: "Geography", path: "/geography", bg: "from-indigo-400 via-blue-500 to-blue-600", ring: "bg-cyan-300" },
      { name: "Stories", path: "/stories", bg: "from-yellow-400 via-amber-400 to-amber-500", ring: "bg-rose-300" },
    ],
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};

const itemVar = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

const LearningGrid = () => {
  return (
    <div className="h-full flex flex-col">
      <h2 className="text-lg md:text-xl font-fredoka font-bold text-center mb-3">
        <span className="text-cyan-700">Start Learning</span>
      </h2>

      <motion.div
        className="flex flex-col gap-3 flex-1"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {rows.map((row) => (
          <div key={row.title}>
            <div className="flex items-baseline justify-between px-1 mb-1.5">
              <span className="text-xs md:text-sm font-fredoka font-bold text-cyan-800">
                {row.title}
              </span>
              <span className="text-[10px] md:text-xs font-fredoka text-cyan-600/80">
                {row.subtitle}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {row.items.map((it) => (
                <motion.div key={it.path} variants={itemVar}>
                  <Link
                    to={it.path}
                    className={`relative overflow-hidden bg-gradient-to-br ${it.bg} rounded-2xl px-2 py-4 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-[1.04] hover:-translate-y-0.5 transition-all duration-200 min-h-[68px] border-2 border-white/40`}
                  >
                    {/* playful decorative blobs */}
                    <span className={`pointer-events-none absolute -top-3 -right-3 w-10 h-10 rounded-full ${it.ring} opacity-40 blur-[1px]`} />
                    <span className={`pointer-events-none absolute -bottom-4 -left-4 w-12 h-12 rounded-full ${it.ring} opacity-25`} />
                    <span className="pointer-events-none absolute top-1.5 left-2 w-8 h-2 rounded-full bg-white/40 blur-[1px]" />

                    <span className="relative text-[12px] md:text-sm font-fredoka font-bold text-white text-center leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
                      {it.name}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default LearningGrid;
